import {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { NPSDContactInfo } from '@/components/npsd/NPSDContactInfo';

interface CodeItem {
  code: string | number;
  value: string;
}

interface NPSDClientDetailsProps {
  client: any;
  isReadOnly?: boolean;
  isAdminReadOnly?: boolean;
  phoneOptionTypes?: any[];
  emailOptionTypes?: any[];
  locationExists?: boolean;
  isCadOrMdt?: boolean;
  onPhonesUpdated?: (phones: any[]) => void;
  onEmailsUpdated?: (emails: any[]) => void;
  onAddGateCode?: () => void;
  onRemoveGateCode?: (gateCode: any) => void;
  onViewMap?: () => void;
  onClientChange?: (client: any) => void;
}

export function NPSDClientDetails({
  client,
  isReadOnly = false,
  isAdminReadOnly = false,
  phoneOptionTypes = [],
  emailOptionTypes = [],
  locationExists = false,
  isCadOrMdt = false,
  onPhonesUpdated,
  onEmailsUpdated,
  onAddGateCode,
  onRemoveGateCode,
  onViewMap,
  onClientChange,
}: NPSDClientDetailsProps) {
  const { theme } = useTheme();

  const [timeZones, setTimeZones] = useState<CodeItem[]>([]);
  const [notificationTimeOptions, setNotificationTimeOptions] = useState<CodeItem[]>([]);

  // Computed disabled flags
  const isClientNameDisabled = isAdminReadOnly;
  const isLocationDisabled = isAdminReadOnly;
  const isContactDisabled = isAdminReadOnly;
  const isGateCodeDisabled = isAdminReadOnly;

  // Convert hour to millis helper
  const convertHourToMillis = useCallback((hour: number): number => {
    const minsInDay = 60 * 24;
    let mins = hour * 60;
    mins = (mins + minsInDay) % minsInDay;
    return 1000 * 60 * mins;
  }, []);

  // Load time zones
  useEffect(() => {
    const stz = (window as any).SelectableTimeZones;
    if (typeof stz !== 'undefined') {
      setTimeZones(stz);
    }
  }, []);

  // Generate notification time options
  useEffect(() => {
    const options: CodeItem[] = [];
    for (let hour = 0; hour < 24; hour++) {
      const hourStr = hour < 10 ? '0' + hour : hour.toString();
      const millis = convertHourToMillis(hour);
      options.push({
        code: millis,
        value: hourStr + ':00',
      });
    }
    setNotificationTimeOptions(options);
  }, [convertHourToMillis]);

  // Format notification time
  const formatNotificationTime = useCallback((millis: number): string => {
    if (millis === null || millis === undefined) return '--:--';
    const totalMinutes = millis / (1000 * 60);
    const hours = Math.floor(totalMinutes / 60) % 24;
    const hourStr = hours < 10 ? '0' + hours : hours.toString();
    return hourStr + ':00';
  }, []);

  // Update client field helper
  const updateClientField = useCallback((field: string, value: any) => {
    if (onClientChange) {
      onClientChange({ ...client, [field]: value });
    }
  }, [client, onClientChange]);

  // Phone/Email update handlers
  const onPhonesUpdate = useCallback((phones: any[]) => {
    if (onClientChange) {
      onClientChange({ ...client, phoneNumbers: phones });
    }
    onPhonesUpdated?.(phones);
  }, [client, onClientChange, onPhonesUpdated]);

  const onEmailsUpdate = useCallback((emails: any[]) => {
    if (onClientChange) {
      onClientChange({ ...client, emails: emails });
    }
    onEmailsUpdated?.(emails);
  }, [client, onClientChange, onEmailsUpdated]);

  // Timezone change handler
  const handleTimeZoneChange = useCallback((val: string | number | null) => {
    if (val !== null) {
      updateClientField('notificationDefaultTimeZoneId', val);
    }
  }, [updateClientField]);

  // Notification time change handler
  const handleNotificationTimeChange = useCallback((val: string | number | null) => {
    if (val !== null) {
      updateClientField('notificationDefaultTime', val);
    }
  }, [updateClientField]);

  // Gate code input change handler
  const handleGateCodeChange = useCallback((index: number, value: string) => {
    if (onClientChange && client.gateCodes) {
      const updatedGateCodes = [...client.gateCodes];
      updatedGateCodes[index] = { ...updatedGateCodes[index], gateCode: value };
      onClientChange({ ...client, gateCodes: updatedGateCodes });
    }
  }, [client, onClientChange]);

  return (
    <div className={`npsd-client-details npsd-entity-details${theme ? ' ' + theme : ''}`}>
      <div className="details-layout">
        {/* Main Column (left) */}
        <div className="details-column-main">
          {/* Client Name Panel */}
          <div className="panel client-name-panel">
            <div className="panel-header">Client Name</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="mandatory">Client Name</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.name || ''}
                    onChange={(e) => updateClientField('name', e.target.value)}
                    disabled={isClientNameDisabled}
                  />
                </div>
                <div className="field-group field-half">
                  <label>Website</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.website || ''}
                    onChange={(e) => updateClientField('website', e.target.value)}
                    disabled={isClientNameDisabled}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label>Account Number</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.accountNumber || ''}
                    onChange={(e) => updateClientField('accountNumber', e.target.value)}
                    disabled={isClientNameDisabled}
                  />
                </div>
                <div className="field-group field-half">
                  <label>Authorization Code</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.authorizationCode || ''}
                    onChange={(e) => updateClientField('authorizationCode', e.target.value)}
                    disabled={isClientNameDisabled}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label>Notes</label>
                  <textarea
                    className="kstc-input"
                    value={client.notes || ''}
                    onChange={(e) => updateClientField('notes', e.target.value)}
                    rows={6}
                    disabled={isClientNameDisabled}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Location Panel */}
          <div className="panel location-panel">
            <div className="panel-header">
              Location
              {locationExists && isCadOrMdt && (
                <button type="button" className="kstc-btn kstc-btn--link" onClick={onViewMap}>
                  View Map
                </button>
              )}
            </div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-full">
                  <label>Address</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.locFormattedAddress || ''}
                    onChange={(e) => updateClientField('locFormattedAddress', e.target.value)}
                    disabled={isLocationDisabled}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label>Apt/Suite</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.locAddress2 || ''}
                    onChange={(e) => updateClientField('locAddress2', e.target.value)}
                    disabled={isLocationDisabled}
                  />
                </div>
                <div className="field-group field-half">
                  <label>Building</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={client.locName || ''}
                    onChange={(e) => updateClientField('locName', e.target.value)}
                    disabled={isLocationDisabled}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notification Defaults Panel (Admin Only) */}
          <div className="panel notification-defaults-panel">
            <div className="panel-header">Notification Defaults</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label>Default Time Zone</label>
                  {!isAdminReadOnly ? (
                    <Dropdownlist
                      value={client.notificationDefaultTimeZoneId}
                      items={timeZones}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select timezone..."
                      onChange={handleTimeZoneChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={client.notificationDefaultTimeZoneId || ''}
                      disabled
                    />
                  )}
                </div>
                <div className="field-group field-half">
                  <label>Default Notification Time</label>
                  {!isAdminReadOnly ? (
                    <Dropdownlist
                      value={client.notificationDefaultTime}
                      items={notificationTimeOptions}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select time..."
                      onChange={handleNotificationTimeChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={formatNotificationTime(client.notificationDefaultTime)}
                      disabled
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side Column (right) */}
        <div className="details-column-side">
          {/* CONTACT Section - Using shared component */}
          <NPSDContactInfo
            phones={client.phoneNumbers}
            emails={client.emails}
            isReadOnly={isContactDisabled}
            phoneTypes={phoneOptionTypes}
            emailTypes={emailOptionTypes}
            title="Contact"
            onPhonesChange={onPhonesUpdate}
            onEmailsChange={onEmailsUpdate}
          />

          {/* Access Panel */}
          <div className="panel access-panel">
            <div className="panel-header">
              Access
              {!isGateCodeDisabled && (
                <button
                  type="button"
                  className="kstc-btn kstc-btn--success kstc-btn--sm"
                  onClick={onAddGateCode}
                >
                  <i className="fas fa-plus"></i> Gate Code
                </button>
              )}
            </div>
            <div className="panel-body">
              <div className="contact-section">
                <div className="contact-label">Gate Code:</div>
                <div className="contact-items">
                  {(!client.gateCodes || client.gateCodes.length === 0) && (
                    <div className="empty-state">
                      No gate codes
                    </div>
                  )}
                  {client.gateCodes && client.gateCodes.map((gateCode: any, index: number) => (
                    <div key={'gate-' + index} className="contact-item">
                      <input
                        type="text"
                        value={gateCode.gateCode || ''}
                        className="kstc-input"
                        placeholder="Enter gate code"
                        disabled={isGateCodeDisabled}
                        onChange={(e) => handleGateCodeChange(index, e.target.value)}
                      />
                      {!isGateCodeDisabled && (
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => onRemoveGateCode?.(gateCode)}
                        >
                          X
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDClientDetailsReact = NPSDClientDetails;
